import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Unity, useUnityContext } from 'react-unity-webgl'
import styles from './app.module.css'
import packageJson from '../package.json'

const TELEPORTATION_OBJECT = 'WebGLBridge'
const TELEPORTATION_METHOD = 'Teleportation'
const MOBILE_USER_AGENT = /iPhone|iPad|iPod|Android/i

interface UnityDiagnosticsMetrics {
  totalWASMHeapSize: number
  usedWASMHeapSize: number
  totalJSHeapSize: number
  usedJSHeapSize: number
  pageLoadTime: number
  pageLoadTimeToFrame1: number
  fps: number
  movingAverageFps: number
  assetLoadTime: number
  webAssemblyStartupTime: number
  codeDownloadTime: number
  gameStartupTime: number
  numJankedFrames: number
}

interface UnityInstanceWithDiagnostics {
  GetMetricsInfo(): UnityDiagnosticsMetrics
}

interface UnityDiagnosticsConfig {
  enabled?: unknown
}

function formatMemory(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return 'N/A'
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatMilliseconds(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return 'N/A'
  return `${Math.round(milliseconds)} ms`
}

function formatFps(fps: number): string {
  if (!Number.isFinite(fps) || fps < 0) return 'N/A'
  return fps.toFixed(1)
}

function buildTeleportationQuery(search: string): string | null {
  const query = search.replace(/^\?/, '')
  if (!query) return null

  const params: Record<string, string> = {}
  for (const pair of query.split(';')) {
    const [key, value] = pair.split('=')
    if (key && value !== undefined) {
      params[decodeURIComponent(key)] = decodeURIComponent(value)
    }
  }

  if (!params.environment || !params.version) {
    return null
  }

  return `?${query}`
}

function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDiagnosticsEnabled, setIsDiagnosticsEnabled] = useState(false)
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false)
  const [diagnosticsMetrics, setDiagnosticsMetrics] =
    useState<UnityDiagnosticsMetrics | null>(null)
  const isMobile = MOBILE_USER_AGENT.test(window.navigator.userAgent)

  const {
    unityProvider,
    isLoaded,
    loadingProgression,
    initialisationError,
    sendMessage,
    UNSAFE__unityInstance,
  } = useUnityContext({
    loaderUrl: `/Build/public.loader.js?v=${packageJson.version}`,
    dataUrl: `/Build/public.data?v=${packageJson.version}`,
    frameworkUrl: `/Build/public.framework.js?v=${packageJson.version}`,
    codeUrl: `/Build/public.wasm?v=${packageJson.version}`,
    productVersion: packageJson.version,
    productName: 'totalYmage',
    companyName: 'Time2Market',
    streamingAssetsUrl: '/StreamingAssets',
    webglContextAttributes: {
      preserveDrawingBuffer: true,
    },
  })

  useLayoutEffect(() => {
    if (!canvasRef.current) return

    // Matches the dimensions emitted by the Unity 6 WebGL template. Unity will
    // keep the render target synchronized with the responsive CSS size.
    canvasRef.current.width = 1920
    canvasRef.current.height = 1080
  }, [])

  useEffect(() => {
    let isDisposed = false

    const loadDiagnosticsConfig = async () => {
      try {
        const response = await fetch(
          `/Build/diagnostics.json?v=${packageJson.version}`,
          { cache: 'no-store' }
        )

        if (!response.ok) return

        const config = (await response.json()) as UnityDiagnosticsConfig
        if (!isDisposed) setIsDiagnosticsEnabled(config.enabled === true)
      } catch {
        // The file is optional. A missing or invalid configuration keeps the
        // diagnostics UI disabled without affecting the Unity application.
      }
    }

    void loadDiagnosticsConfig()

    return () => {
      isDisposed = true
    }
  }, [])

  useEffect(() => {
    if (!isLoaded) return

    console.log(
      '[Teleportation] Unity loaded — initial search recorded (no send on first load):',
      window.location.search
    )

    let lastSearch = window.location.search

    const handleLocationChange = () => {
      const current = window.location.search
      console.log('[Teleportation] location change detected:', {
        from: lastSearch,
        to: current,
      })
      if (current === lastSearch) {
        console.log('[Teleportation] search unchanged, skipping')
        return
      }
      lastSearch = current
      const payload = buildTeleportationQuery(current)
      if (!payload) {
        console.log(
          '[Teleportation] missing required params (environment/version), not sending'
        )
        return
      }
      console.log(
        `[Teleportation] sending to Unity ${TELEPORTATION_OBJECT}.${TELEPORTATION_METHOD}:`,
        payload
      )
      sendMessage(TELEPORTATION_OBJECT, TELEPORTATION_METHOD, payload)
    }

    const origPushState = window.history.pushState
    const origReplaceState = window.history.replaceState
    window.history.pushState = function (...args) {
      origPushState.apply(this, args)
      handleLocationChange()
    }
    window.history.replaceState = function (...args) {
      origReplaceState.apply(this, args)
      handleLocationChange()
    }
    window.addEventListener('popstate', handleLocationChange)

    const handleMessage = (event: MessageEvent) => {
      const data = event.data
      if (
        !data ||
        data.type !== 'teleportation' ||
        typeof data.search !== 'string'
      ) {
        return
      }
      console.log('[Teleportation] postMessage received:', data.search)
      window.history.pushState({}, '', data.search)
    }
    window.addEventListener('message', handleMessage)

    console.log(
      '[Teleportation] listeners attached (pushState/replaceState/popstate/message)'
    )

    return () => {
      window.history.pushState = origPushState
      window.history.replaceState = origReplaceState
      window.removeEventListener('popstate', handleLocationChange)
      window.removeEventListener('message', handleMessage)
      console.log('[Teleportation] listeners detached')
    }
  }, [isLoaded, sendMessage])

  const diagnosticsInstance = UNSAFE__unityInstance as
    | UnityInstanceWithDiagnostics
    | null
  const supportsDiagnostics =
    isDiagnosticsEnabled &&
    isLoaded &&
    typeof diagnosticsInstance?.GetMetricsInfo === 'function'

  useEffect(() => {
    if (!isDiagnosticsOpen || !supportsDiagnostics || !diagnosticsInstance) {
      return
    }

    let isDisposed = false

    const updateMetrics = () => {
      try {
        const metrics = diagnosticsInstance.GetMetricsInfo()
        if (!isDisposed) setDiagnosticsMetrics(metrics)
      } catch (error) {
        console.error('[Diagnostics] Unable to read Unity metrics:', error)
      }
    }

    updateMetrics()
    const intervalId = window.setInterval(updateMetrics, 1000)

    return () => {
      isDisposed = true
      window.clearInterval(intervalId)
    }
  }, [diagnosticsInstance, isDiagnosticsOpen, supportsDiagnostics])

  const errorMessage = initialisationError?.message

  return (
    <main className={styles.page}>
      <div
        id="unity-container"
        className={`${styles.unityContainer} ${
          isMobile ? styles.unityMobile : styles.unityDesktop
        }`}
      >
        <Unity
          ref={canvasRef}
          id="unity-canvas"
          unityProvider={unityProvider}
          className={styles.unityCanvas}
          devicePixelRatio={window.devicePixelRatio}
          tabIndex={-1}
        />

        {!isLoaded && !errorMessage && (
          <div id="unity-loading-bar" className={styles.loadingBar}>
            <div
              id="unity-logo"
              className={styles.unityLogo}
              style={{
                backgroundImage:
                  "url('/TemplateData/totalymage_logo.png')",
              }}
            />
            <div
              id="unity-progress-bar-empty"
              className={styles.progressBarEmpty}
              role="progressbar"
              aria-label="Chargement"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(loadingProgression * 100)}
            >
              <div
                id="unity-progress-bar-full"
                className={styles.progressBarFull}
                style={{
                  width: `${loadingProgression * 100}%`,
                }}
              />
            </div>
            <div className={styles.loadingProgressLabel}>
              Chargement… {Math.round(loadingProgression * 100)}%
            </div>
          </div>
        )}

        <div
          id="unity-warning"
          className={styles.warning}
          aria-live="assertive"
        >
          {errorMessage && <div className={styles.errorBanner}>{errorMessage}</div>}
        </div>

        {supportsDiagnostics && (
          <>
            <button
              className={styles.diagnosticsButton}
              type="button"
              aria-controls="unity-diagnostics-overlay"
              aria-expanded={isDiagnosticsOpen}
              onClick={() => setIsDiagnosticsOpen((isOpen) => !isOpen)}
            >
              Diagnostics
            </button>

            {isDiagnosticsOpen && (
              <section
                id="unity-diagnostics-overlay"
                className={styles.diagnosticsOverlay}
                role="dialog"
                aria-label="Unity Web diagnostics"
              >
                <header className={styles.diagnosticsHeader}>
                  <h2>Unity Web diagnostics</h2>
                  <button
                    className={styles.diagnosticsCloseButton}
                    type="button"
                    aria-label="Close diagnostics"
                    onClick={() => setIsDiagnosticsOpen(false)}
                  >
                    ×
                  </button>
                </header>

                {diagnosticsMetrics ? (
                  <dl className={styles.diagnosticsGrid}>
                    <dt>Used JS memory</dt>
                    <dd>{formatMemory(diagnosticsMetrics.usedJSHeapSize)}</dd>
                    <dt>Total JS memory</dt>
                    <dd>{formatMemory(diagnosticsMetrics.totalJSHeapSize)}</dd>
                    <dt>Used WASM heap</dt>
                    <dd>{formatMemory(diagnosticsMetrics.usedWASMHeapSize)}</dd>
                    <dt>Total WASM heap</dt>
                    <dd>{formatMemory(diagnosticsMetrics.totalWASMHeapSize)}</dd>
                    <dt>Current FPS</dt>
                    <dd>{formatFps(diagnosticsMetrics.fps)}</dd>
                    <dt>Average FPS</dt>
                    <dd>{formatFps(diagnosticsMetrics.movingAverageFps)}</dd>
                    <dt>Frame stalls</dt>
                    <dd>{diagnosticsMetrics.numJankedFrames}</dd>
                    <dt>Load to first frame</dt>
                    <dd>
                      {formatMilliseconds(
                        diagnosticsMetrics.pageLoadTimeToFrame1
                      )}
                    </dd>
                    <dt>Page load</dt>
                    <dd>{formatMilliseconds(diagnosticsMetrics.pageLoadTime)}</dd>
                    <dt>Code download</dt>
                    <dd>
                      {formatMilliseconds(diagnosticsMetrics.codeDownloadTime)}
                    </dd>
                    <dt>Asset load</dt>
                    <dd>{formatMilliseconds(diagnosticsMetrics.assetLoadTime)}</dd>
                    <dt>WebAssembly startup</dt>
                    <dd>
                      {formatMilliseconds(
                        diagnosticsMetrics.webAssemblyStartupTime
                      )}
                    </dd>
                    <dt>Game startup</dt>
                    <dd>{formatMilliseconds(diagnosticsMetrics.gameStartupTime)}</dd>
                  </dl>
                ) : (
                  <p className={styles.diagnosticsLoading}>
                    Collecting diagnostics…
                  </p>
                )}
              </section>
            )}
          </>
        )}

      </div>
    </main>
  )
}

export default App
